import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { cn } from '@/lib/cn';

/**
 * Standard page header — back link + breadcrumb + title + subtitle + meta
 * (status badges) + action buttons. Encapsulates the mobile shrink pattern
 * (smaller h1, drop subtitle, wrap action cluster) so individual pages
 * don't each re-invent it. Skipping it is fine when a page needs a
 * one-off layout (e.g. an avatar-led header) — this primitive is a
 * convenience for the common case.
 *
 * Mobile rules:
 *   - Title scales from text-2xl down to text-lg below `sm:`.
 *   - Subtitle remains visible but tightens to text-xs.
 *   - meta + actions wrap to a new line when the row overflows.
 *   - Back link sits above the title on a row of its own so it never
 *     fights the action cluster for horizontal space.
 */

type BackProp =
    | { to: string; label?: string }
    | { onClick: () => void; label?: string };

export interface PageHeaderProps {
    title: ReactNode;
    subtitle?: ReactNode;
    /** Pre-title context line — usually small, neutral text (e.g.
     *  "Configuration · Note Types"). */
    breadcrumb?: ReactNode;
    /** Back affordance. Pass a `to:` to render as a Link, or `onClick:` for
     *  navigate(-1) / dialog dismiss. */
    back?: BackProp;
    /** Right-aligned status badges or other small metadata. Wraps below the
     *  title on narrow viewports. */
    meta?: ReactNode;
    /** Right-aligned action buttons. Wraps below meta on narrow viewports. */
    actions?: ReactNode;
    className?: string;
}

export function PageHeader({
    title, subtitle, breadcrumb, back, meta, actions, className,
}: PageHeaderProps) {
    return (
        <div className={cn('mb-4 sm:mb-5 space-y-2', className)}>
            {back && <BackAffordance back={back} />}
            {breadcrumb && (
                <div className="text-xs text-text-subtle">{breadcrumb}</div>
            )}
            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
                <div className="min-w-0 flex-1">
                    <h1 className="font-display text-lg sm:text-2xl font-bold text-text">
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="text-xs text-text-subtle mt-0.5">{subtitle}</p>
                    )}
                </div>
                {meta && (
                    <div className="flex flex-wrap items-center gap-x-3 sm:gap-x-5 gap-y-1.5">
                        {meta}
                    </div>
                )}
                {actions && (
                    <div className="flex flex-wrap items-center gap-2">
                        {actions}
                    </div>
                )}
            </div>
        </div>
    );
}

function BackAffordance({ back }: { back: BackProp }) {
    const label = back.label ?? 'Back';
    const className = 'inline-flex items-center gap-1 text-xs text-text-muted hover:text-text';
    if ('to' in back) {
        return (
            <Link to={back.to} className={className}>
                <ChevronLeft className="w-3 h-3" /> {label}
            </Link>
        );
    }
    return (
        <button type="button" onClick={back.onClick} className={className}>
            <ChevronLeft className="w-3 h-3" /> {label}
        </button>
    );
}
